import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, ClipboardList, Trash2, Play, Pause, CheckCircle2, XCircle, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "planned",     label: "Planned",     color: "bg-slate-200 text-slate-800" },
  { value: "released",    label: "Released",    color: "bg-blue-100 text-blue-800" },
  { value: "in_progress", label: "In progress", color: "bg-amber-100 text-amber-800" },
  { value: "paused",      label: "Paused",      color: "bg-purple-100 text-purple-800" },
  { value: "complete",    label: "Complete",    color: "bg-emerald-100 text-emerald-800" },
  { value: "cancelled",   label: "Cancelled",   color: "bg-red-100 text-red-800" },
];
const PRIORITIES = [
  { value: "low",    label: "Low",    color: "bg-secondary text-foreground" },
  { value: "normal", label: "Normal", color: "bg-blue-100 text-blue-800" },
  { value: "high",   label: "High",   color: "bg-amber-100 text-amber-800" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-800" },
];
const EMPTY = { wo_number: "", product: "", quantity: "", unit: "ea", priority: "normal", machine_id: "", assigned_to: "", due_date: "", notes: "" };

export default function GmesWorkOrders() {
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/gmes/work-orders").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/gmes/machines").then(({ data }) => setMachines(data)).catch(() => {});
  }, []);

  const machineMap = useMemo(() => Object.fromEntries(machines.map((m) => [m.id, m.name])), [machines]);
  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = STATUSES.reduce((acc, s) => { acc[s.value] = items.filter((i) => i.status === s.value).length; return acc; }, {});

  const save = async (e) => {
    e.preventDefault();
    if (!form.product.trim() || !form.quantity) { toast.error("Product and quantity required"); return; }
    setSaving(true);
    try {
      await api.post("/gmes/work-orders", { ...form, quantity: parseFloat(form.quantity) });
      toast.success("Work order created");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const transition = async (id, status) => {
    try {
      await api.patch(`/gmes/work-orders/${id}/transition`, { status });
      toast.success(`Moved to ${status}`);
      load();
    } catch { toast.error("Transition failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this work order?")) return;
    await api.delete(`/gmes/work-orders/${id}`);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/shopfloor" className="hover:text-primary">Shop Floor</Link> / Work Orders
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="gmes-wo-title">
            Work Orders
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Plan · release · start · pause · complete. Production logs and scrap roll up automatically.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-wo-btn">
          <Plus className="w-4 h-4 mr-2" /> New work order
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-1.5 mb-6">
        <button onClick={() => setFilter("all")} className={`rounded-sm px-3 py-1.5 text-xs font-mono-plex uppercase tracking-widest ${filter === "all" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/70"}`} data-testid="wo-filter-all">
          All · {items.length}
        </button>
        {STATUSES.map((s) => (
          <button key={s.value} onClick={() => setFilter(s.value)}
                  className={`rounded-sm px-3 py-1.5 text-xs font-mono-plex uppercase tracking-widest ${filter === s.value ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/70"}`}
                  data-testid={`wo-filter-${s.value}`}>
            {s.label} · {counts[s.value] || 0}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <ClipboardList className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No work orders</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Create your first work order to schedule production against a machine.
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                {["WO #", "Product", "Qty", "Priority", "Machine", "Status", "Progress", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => {
                const status = STATUSES.find((s) => s.value === w.status) || STATUSES[0];
                const priority = PRIORITIES.find((p) => p.value === w.priority) || PRIORITIES[1];
                const pct = w.quantity > 0 ? Math.min(100, Math.round((w.produced_qty || 0) / w.quantity * 100)) : 0;
                return (
                  <tr key={w.id} className="border-t border-border" data-testid={`wo-row-${w.id}`}>
                    <td className="px-3 py-2 font-mono-plex text-xs">{w.wo_number}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{w.product}</div>
                      {w.assigned_to && <div className="text-xs text-muted-foreground">→ {w.assigned_to}</div>}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{w.quantity} <span className="text-muted-foreground text-xs">{w.unit}</span></td>
                    <td className="px-3 py-2"><Badge className={`rounded-sm ${priority.color} hover:${priority.color} font-mono-plex text-[10px] uppercase tracking-widest`}>{priority.label}</Badge></td>
                    <td className="px-3 py-2 text-xs">{machineMap[w.machine_id] || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2"><Badge className={`rounded-sm ${status.color} hover:${status.color} font-mono-plex text-[10px] uppercase tracking-widest`}>{status.label}</Badge></td>
                    <td className="px-3 py-2 w-40">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-secondary rounded-sm overflow-hidden">
                          <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] font-mono-plex text-muted-foreground tabular-nums w-10 text-right">{pct}%</span>
                      </div>
                      {w.scrap_qty > 0 && <div className="text-[10px] text-red-600 mt-1">scrap {w.scrap_qty}</div>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {w.status === "planned" && (
                        <button title="Release" onClick={() => transition(w.id, "released")} className="p-1 text-blue-600 hover:bg-blue-50 rounded-sm" data-testid={`wo-release-${w.id}`}>
                          <ArrowUpRight className="w-4 h-4" />
                        </button>
                      )}
                      {(w.status === "released" || w.status === "paused") && (
                        <button title="Start" onClick={() => transition(w.id, "in_progress")} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-sm" data-testid={`wo-start-${w.id}`}>
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      {w.status === "in_progress" && (
                        <>
                          <button title="Pause" onClick={() => transition(w.id, "paused")} className="p-1 text-amber-600 hover:bg-amber-50 rounded-sm" data-testid={`wo-pause-${w.id}`}>
                            <Pause className="w-4 h-4" />
                          </button>
                          <button title="Complete" onClick={() => transition(w.id, "complete")} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-sm" data-testid={`wo-complete-${w.id}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {w.status !== "complete" && w.status !== "cancelled" && (
                        <button title="Cancel" onClick={() => transition(w.id, "cancelled")} className="p-1 text-red-600 hover:bg-red-50 rounded-sm" data-testid={`wo-cancel-${w.id}`}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button title="Delete" onClick={() => remove(w.id)} className="p-1 text-muted-foreground hover:text-destructive hover:bg-red-50 rounded-sm ml-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="overline text-accent">◉ NEW WORK ORDER</div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Schedule production</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">WO # (auto if blank)</label>
                <Input value={form.wo_number} onChange={(e) => setForm({ ...form, wo_number: e.target.value })} className="rounded-sm" placeholder="WO-1001" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Product / part *</label>
              <Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="rounded-sm" data-testid="wo-product-input" required />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Qty *</label>
                <Input type="number" step="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="rounded-sm" data-testid="wo-qty-input" required />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Unit</label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-sm" placeholder="ea, kg, ft, m…" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Due date</label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Machine</label>
                <Select value={form.machine_id || "none"} onValueChange={(v) => setForm({ ...form, machine_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Assigned operator</label>
                <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="rounded-sm" placeholder="Name" />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm resize-none" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-wo-btn">
              {saving ? "Creating…" : "Create work order"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
